import { CollapseIcon } from 'module/ui/components/atoms/collapse/collapse-icon';
import _, { range } from 'lodash';
import {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { components } from 'react-select';
import { VariableSizeList as VariableWindowList } from 'react-window';
import {
  GroupIndicatorIcon,
  AccordionHeader,
  StickyHeaderWrapper,
} from './components';
import { optionHeight } from '../../atoms/select/constants';
import { SelectMode } from '../../atoms/select/types';

const VirtualizationContext = createContext({} as any);

const GroupHeading = (props) => {
  const { data, label } = props;

  return (
    <components.GroupHeading {...props}>
      {label} ({data?.options?.length ?? 0})
    </components.GroupHeading>
  );
};

const StickyRow = ({ style, children }) => (
  <StickyHeaderWrapper style={style}>
    <div className="sticky">{children}</div>
  </StickyHeaderWrapper>
);

// List Virtualization
const computeMenuItemHeight = (option, type: SelectMode = 'basic') => {
  if (option.type === 'groupHeading') {
    return optionHeight.groupHeading;
  }

  if (type === 'user') {
    return option.description
      ? optionHeight.userDescription
      : optionHeight.user;
  }

  return optionHeight.basic;
};

const flattenGroupedChildrens = (
  children: React.ReactElement[],
  activeIndices: number[],
) => {
  return children
    .filter((e) => _.isArray(e?.props?.children) && e?.props?.children?.length)
    .map((child, index) => [
      {
        props: {
          data: {
            type: 'groupHeading',
            label: child.props?.data?.label,
          },
        },
      },
      ...(activeIndices.includes(index) ? (child.props?.children ?? []) : []),
    ])
    .reduce(
      (accumulator, currentValue) => accumulator.concat(currentValue),
      [],
    );
};

const RowRenderer = ({ index, style, data }) => (
  <div style={style}>
    {data[index]?.props?.data?.type === 'groupHeading' ? null : data[index]}
  </div>
);

const getWrapperHeightForStickyHeader = (options): number =>
  options.reduce(
    (prev, _option) => prev + computeMenuItemHeight(_option.data),
    optionHeight.groupHeading,
  );

const getWrapperHeightForStickyHeaderWithCustomFunction = (
  options,
  func: (op) => number,
): number =>
  options.reduce(
    (prev, _option) => prev + func(_option.data),
    optionHeight.groupHeading,
  );

const innerElementType = forwardRef(
  ({ children, ...rest }: { children: React.ReactNode }) => {
    const getTop = useCallback(
      (index: number, stickyWrapperHeights: number[]): number => {
        return (
          stickyWrapperHeights
            .slice(0, index)
            .reduce((prev, current) => prev + current, 0) || 0
        );
      },
      [],
    );

    return (
      <VirtualizationContext.Consumer>
        {({
          groups: rawGroups,
          activeIndices,
          onHeadingClick,
          groupHeadingExtraProps,
          grouped,
          itemSize,
        }) => {
          const stickyWrapperHeights: number[] = [];
          const groups = rawGroups.filter((_group) =>
            _.isArray(_group?.props?.children),
          );

          return (
            <div {...rest}>
              {grouped &&
                groups.map((_group, index) => {
                  const {
                    children: _children,
                    data,
                    ...headingPropsRest
                  } = _group.props;

                  const _options = _children.map((_child) => _child.props);

                  const isActive = activeIndices.includes(index);
                  const height = isActive
                    ? itemSize
                      ? getWrapperHeightForStickyHeaderWithCustomFunction(
                          _options,
                          itemSize,
                        )
                      : getWrapperHeightForStickyHeader(_options)
                    : itemSize
                      ? itemSize({ type: 'groupHeading' })
                      : optionHeight.groupHeading;

                  stickyWrapperHeights.push(height);

                  return (
                    <StickyRow
                      key={index}
                      style={{
                        top: `${getTop(index, stickyWrapperHeights)}px`,
                        height,
                      }}
                    >
                      <AccordionHeader onClick={() => onHeadingClick(index)}>
                        <CollapseIcon className={isActive ? 'active' : ''} />
                        <GroupHeading
                          {...headingPropsRest}
                          data={data}
                          {...groupHeadingExtraProps}
                        />
                        {data?.icon && (
                          <GroupIndicatorIcon>{data?.icon}</GroupIndicatorIcon>
                        )}
                      </AccordionHeader>
                    </StickyRow>
                  );
                })}
              {children}
            </div>
          );
        }}
      </VirtualizationContext.Consumer>
    );
  },
);

innerElementType.displayName = 'innerElementType';

const VariableListRowRenderer = (props) => {
  const {
    children,
    selectProps: { maxMenuHeight, grouped, itemSize, type = 'basic' },
    groupHeadingExtraProps,
  } = props;

  const [activeIndices, setActiveIndices] = useState(range(children.length));

  const onHeadingClick = useCallback(
    (index: any) => {
      setActiveIndices(
        activeIndices.includes(index)
          ? activeIndices.filter((i) => index !== i)
          : [...activeIndices, index],
      );
    },
    [activeIndices],
  );

  const transformedChild = useMemo(
    () =>
      grouped ? flattenGroupedChildrens(children, activeIndices) : children,
    [activeIndices, children, grouped],
  );

  const listRef = useRef<VariableWindowList>(null);

  useEffect(() => {
    const resetWindowedListCachePosition = () => {
      transformedChild.forEach((_e, index) => {
        if (listRef.current) {
          listRef.current.resetAfterIndex(index);
        }
      });
    };

    resetWindowedListCachePosition();
  }, [transformedChild]);

  return (
    <VirtualizationContext.Provider
      value={{
        groups: children,
        activeIndices,
        onHeadingClick,
        groupHeadingExtraProps,
        grouped,
        itemSize,
      }}
    >
      <VariableWindowList
        height={maxMenuHeight}
        itemCount={transformedChild.length}
        innerElementType={innerElementType}
        itemData={transformedChild}
        ref={listRef}
        itemSize={(index: number) =>
          itemSize
            ? itemSize(transformedChild[index]?.props?.data)
            : computeMenuItemHeight(
                transformedChild[index]?.props?.data || [],
                type,
              )
        }
        width="100%"
      >
        {RowRenderer}
      </VariableWindowList>
    </VirtualizationContext.Provider>
  );
};

export const VirtualizedMenuList = (props): React.JSX.Element => {
  const { children } = props;

  // For handling no options
  if (children && !Array.isArray(children)) {
    return children;
  }

  return <VariableListRowRenderer {...props} />;
};
